import {
  AssociationEntity,
  crmExternalSystems,
  EntitySyncInfoResponse,
  ExternalSystem,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useMemo } from 'react';
import { useGetEntitySyncInfo } from '../../api/associationsService';
import { useHasCrmConfigured } from '../../api/settingsService';
import {
  getExternalRecordUrl,
  useCrmRecordLinkConfig,
} from '../IntegrationLog';

export interface CrmOpportunityLink {
  system: ExternalSystem;
  /** The record id in the CRM (e.g. Salesforce Opportunity, HubSpot Deal). */
  id: string;
  url?: string;
}

const selectCrmAssociation = ({ associations }: EntitySyncInfoResponse) =>
  associations.find(({ externalSystem }) =>
    crmExternalSystems.has(externalSystem),
  ) ?? null;

/**
 * The CRM record an opportunity is linked to, with a deep link when one can
 * be resolved. Reads the opportunity's associations — the same `syncInfo`
 * query the modal header's ExternalAssociationsIcons makes, so this costs no
 * extra request. The association's own `externalObjectUrl` wins; it isn't
 * populated for every system yet (HubSpot), so otherwise the URL is built from
 * the CRM connection.
 *
 * Falls back to `customId` as a Salesforce id when there is no association,
 * since linking from the opportunity modal sets that directly.
 */
export function useCrmOpportunityLink({
  opportunityId,
  customId,
  enabled = true,
}: {
  opportunityId: string;
  customId?: Maybe<string>;
  enabled?: boolean;
}): CrmOpportunityLink | undefined {
  const crmRecordLinkConfig = useCrmRecordLinkConfig();
  const { data: crmConfigData } = useHasCrmConfigured({
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnMount: false,
  });
  const { data: association } = useGetEntitySyncInfo(
    { entity: AssociationEntity.OPPORTUNITY, recordId: opportunityId },
    {
      enabled: enabled && !!opportunityId && !!crmConfigData?.crmConnected,
      retry: false,
      select: selectCrmAssociation,
    },
  );

  return useMemo(() => {
    if (association) {
      const { externalSystem, externalObjectId, externalObjectUrl } =
        association;
      return {
        system: externalSystem,
        id: externalObjectId,
        url:
          externalObjectUrl ||
          getExternalRecordUrl(
            externalSystem,
            'Opportunity',
            externalObjectId,
            crmRecordLinkConfig,
          ),
      };
    }

    if (customId && crmRecordLinkConfig.salesforceInstanceUrl) {
      return {
        system: ExternalSystem.SALESFORCE,
        id: customId,
        url: getExternalRecordUrl(
          ExternalSystem.SALESFORCE,
          'Opportunity',
          customId,
          crmRecordLinkConfig,
        ),
      };
    }

    return undefined;
  }, [association, customId, crmRecordLinkConfig]);
}
